import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    Box,
    Container,
    Heading,
    Text,
    Grid,
    Card,
    Badge,
    Button,
    VStack,
    HStack,
    Spinner,
    Flex,
    Dialog,
    IconButton,
    Input,
    Textarea,
    Stat,
    Tabs
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../services/dataService';
import { branchDataService } from '../services/branchDataService';
import ViewAgentDetailsModal from '../components/ViewAgentDetailsModal';
import EditAgentModal from '../components/EditAgentModal';
import { FiX, FiPlus, FiSearch, FiTrash2, FiRefreshCw, FiEdit2, FiEye } from 'react-icons/fi';

const Blacklist = () => {
    const { hasRole, canEditMain, canEditBranch } = useAuth();
    const [blacklistAgents, setBlacklistAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({ total: 0, from_agentlist: 0, from_queue: 0, manual_entries: 0 });

    // Tab state
    const [activeTab, setActiveTab] = useState('main');

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');

    // Modal states
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);

    // Add form state
    const [newAgent, setNewAgent] = useState({
        country: '',
        agent_name: '',
        blacklist_reason: ''
    });

    // Tab change handler
    const handleTabChange = (details) => {
        const newTab = details.value;
        setActiveTab(newTab);
        setBlacklistAgents([]);
        setPage(1);
        setSearchQuery('');
        setError(null);
    };

    const loadBlacklistAgents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const service = activeTab === 'branch' ? branchDataService : dataService;
            const response = await service.getBlacklistAgents(page, 50, searchQuery);
            setBlacklistAgents(response.data.data);
            setTotalPages(response.data.pagination.totalPages);
        } catch (error) {
            console.error('Error loading blacklist:', error);
            setError('Failed to load blacklist');
        } finally {
            setLoading(false);
        }
    }, [page, searchQuery, activeTab]);

    const loadStats = useCallback(async () => {
        try {
            const service = activeTab === 'branch' ? branchDataService : dataService;
            const response = await service.getBlacklistStats();
            setStats(response.data);
        } catch (error) {
            console.error('Error loading blacklist stats:', error);
        }
    }, [activeTab]);

    useEffect(() => {
        loadBlacklistAgents();
        loadStats();
    }, [loadBlacklistAgents, loadStats]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        loadBlacklistAgents();
    };

    const handleViewDetails = (agent) => {
        setSelectedAgent(agent);
        setViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedAgent(null);
    };

    const handleEditAgent = (agent) => {
        setSelectedAgent(agent);
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedAgent(null);
    };

    const handleSaveAgent = (updatedAgent) => {
        setBlacklistAgents(prev =>
            prev.map(agent =>
                agent.id === updatedAgent.id ? { ...agent, ...updatedAgent } : agent
            )
        );
    };

    const handleAddToBlacklist = async () => {
        if (!newAgent.country || !newAgent.agent_name) {
            alert('Please fill in Country and Agent Name');
            return;
        }

        try {
            const service = activeTab === 'branch' ? branchDataService : dataService;
            await service.addToBlacklist(newAgent);
            setAddModalOpen(false);
            setNewAgent({ country: '', agent_name: '', blacklist_reason: '' });
            loadBlacklistAgents();
            loadStats();
            alert('Agent added to blacklist successfully');
        } catch (error) {
            console.error('Error adding to blacklist:', error);
            alert('Failed to add to blacklist: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleRestoreFromBlacklist = async (agent) => {
        const confirmRestore = window.confirm(
            `Are you sure you want to restore "${agent.agent_name}" from the blacklist?\n\nThis will move the agent back to ${agent.source_table === 'pending_queue' ? 'Pending Queue' : 'Agent List'}.`
        );

        if (!confirmRestore) return;

        try {
            const service = activeTab === 'branch' ? branchDataService : dataService;
            await service.restoreFromBlacklist(agent.id);
            loadBlacklistAgents();
            loadStats();
            alert(`Agent "${agent.agent_name}" restored successfully`);
        } catch (error) {
            console.error('Error restoring from blacklist:', error);
            alert('Failed to restore: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleDeleteFromBlacklist = async (agent) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to PERMANENTLY DELETE "${agent.agent_name}" from the blacklist?\n\nThis action cannot be undone!`
        );

        if (!confirmDelete) return;

        try {
            const service = activeTab === 'branch' ? branchDataService : dataService;
            await service.deleteFromBlacklist(agent.id);
            loadBlacklistAgents();
            loadStats();
            alert(`Agent "${agent.agent_name}" deleted permanently`);
        } catch (error) {
            console.error('Error deleting from blacklist:', error);
            if (error.response?.status === 403) {
                alert('Only Admins can permanently delete from blacklist');
            } else {
                alert('Failed to delete: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                timeZone: 'Asia/Jakarta',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const getSourceBadge = (sourceTable) => {
        switch (sourceTable) {
            case 'agentlist':
                return <Badge colorPalette="blue">From Agent List</Badge>;
            case 'pending_queue':
                return <Badge colorPalette="orange">From Queue</Badge>;
            default:
                return <Badge colorPalette="gray">Manual Entry</Badge>;
        }
    };

    return (
        <Box minH="100vh" bg="gray.50" py={8}>
            <Container maxW="container.xl">
                <VStack spacing={8} align="stretch">
                    {/* Header - Light Theme */}
                    <Box
                        textAlign="center"
                        py={4}
                    >
                        <Heading
                            as="h1"
                            size="2xl"
                            color="red.600"
                            mb={3}
                        >
                            BLACKLIST
                        </Heading>
                        <Text fontSize="xl" color="gray.500">
                            Manage blacklisted agents - Handle with caution
                        </Text>
                    </Box>

                    {/* Main/Branch Tabs */}
                    <Tabs.Root defaultValue="main" value={activeTab} onValueChange={handleTabChange}>
                        <Tabs.List mb={4}>
                            <Tabs.Trigger value="main" px={6} py={2}>
                                <Text fontWeight="medium">Main Blacklist</Text>
                            </Tabs.Trigger>
                            <Tabs.Trigger value="branch" px={6} py={2}>
                                <Text fontWeight="medium">Branch Blacklist</Text>
                            </Tabs.Trigger>
                            <Tabs.Indicator />
                        </Tabs.List>
                    </Tabs.Root>

                    {/* Stats Cards - Light Theme */}
                    <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={4}>
                        <Card.Root shadow="md" borderRadius="lg" bg="white" border="1px solid" borderColor="red.100">
                            <Card.Body p={4}>
                                <Stat.Root>
                                    <Stat.Label color="red.600" fontWeight="bold">Total Blacklisted</Stat.Label>
                                    <Stat.ValueText fontSize="2xl" color="red.700">{stats.total}</Stat.ValueText>
                                </Stat.Root>
                            </Card.Body>
                        </Card.Root>
                        <Card.Root shadow="md" borderRadius="lg" bg="white" border="1px solid" borderColor="blue.100">
                            <Card.Body p={4}>
                                <Stat.Root>
                                    <Stat.Label color="blue.600" fontWeight="bold">From Agent List</Stat.Label>
                                    <Stat.ValueText fontSize="2xl" color="blue.700">{stats.from_agentlist}</Stat.ValueText>
                                </Stat.Root>
                            </Card.Body>
                        </Card.Root>
                        <Card.Root shadow="md" borderRadius="lg" bg="white" border="1px solid" borderColor="orange.100">
                            <Card.Body p={4}>
                                <Stat.Root>
                                    <Stat.Label color="orange.600" fontWeight="bold">From Queue</Stat.Label>
                                    <Stat.ValueText fontSize="2xl" color="orange.700">{stats.from_queue}</Stat.ValueText>
                                </Stat.Root>
                            </Card.Body>
                        </Card.Root>
                        <Card.Root shadow="md" borderRadius="lg" bg="white" border="1px solid" borderColor="gray.100">
                            <Card.Body p={4}>
                                <Stat.Root>
                                    <Stat.Label color="gray.600" fontWeight="bold">Manual Entries</Stat.Label>
                                    <Stat.ValueText fontSize="2xl" color="gray.700">{stats.manual_entries}</Stat.ValueText>
                                </Stat.Root>
                            </Card.Body>
                        </Card.Root>
                    </Grid>

                    {/* Search and Add - Light Theme */}
                    <Card.Root shadow="md" borderRadius="xl" bg="white" border="1px solid" borderColor="gray.100">
                        <Card.Body p={6}>
                            <Flex gap={4} direction={{ base: 'column', md: 'row' }} align="center">
                                <form onSubmit={handleSearch} style={{ flex: 1, display: 'flex', gap: '8px' }}>
                                    <Input
                                        placeholder="Search by country, agent name, or reason..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        flex={1}
                                        bg="gray.50"
                                        borderColor="gray.200"
                                        _focus={{ borderColor: 'red.500', boxShadow: '0 0 0 1px var(--chakra-colors-red-500)' }}
                                    />
                                    <Button type="submit" colorPalette="red" variant="solid">
                                        <FiSearch /> Search
                                    </Button>
                                </form>
                                {((activeTab === 'main' && canEditMain()) || (activeTab === 'branch' && canEditBranch())) && (
                                    <Button colorPalette="red" variant="solid" onClick={() => setAddModalOpen(true)}>
                                        <FiPlus /> Add to Blacklist
                                    </Button>
                                )}
                            </Flex>
                        </Card.Body>
                    </Card.Root>

                    {/* Error Message - Light Theme */}
                    {error && (
                        <Box bg="red.50" color="red.600" p={4} borderRadius="md" border="1px solid" borderColor="red.200">
                            {error}
                        </Box>
                    )}

                    {/* Loading - Light Theme */}
                    {loading && (
                        <Flex justify="center" align="center" py={10}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color="red.500" thickness="4px" />
                                <Text color="gray.500" fontWeight="medium">Loading blacklist...</Text>
                            </VStack>
                        </Flex>
                    )}

                    {/* Empty State - Light Theme */}
                    {!loading && blacklistAgents.length === 0 && !error && (
                        <Card.Root shadow="md" borderRadius="xl" bg="white" borderTop="4px" borderTopColor="red.500">
                            <Card.Body p={8}>
                                <Box textAlign="center" py={10} color="gray.400">
                                    <Text fontSize="6xl" mb={4}>🚫</Text>
                                    <Heading size="md" mb={2} color="gray.600">No Blacklisted Agents</Heading>
                                    <Text>The blacklist is empty. No agents have been blacklisted yet.</Text>
                                </Box>
                            </Card.Body>
                        </Card.Root>
                    )}

                    {/* Blacklist Grid - Light Theme */}
                    {blacklistAgents.length > 0 && (
                        <Box>
                            <Flex justify="space-between" align="center" mb={6}>
                                <Heading size="lg" color="red.600">Blacklisted Agents</Heading>
                                <Text color="gray.500">
                                    {blacklistAgents.length} agent{blacklistAgents.length !== 1 ? 's' : ''} shown
                                </Text>
                            </Flex>

                            <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
                                {blacklistAgents.map((agent) => (
                                    <Card.Root
                                        key={agent.id}
                                        shadow="md"
                                        borderRadius="xl"
                                        bg="white"
                                        _hover={{ shadow: 'xl', transform: 'translateY(-4px)', borderColor: 'red.300' }}
                                        transition="all 0.3s"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        borderTop="4px"
                                        borderTopColor="red.500"
                                    >
                                        <Card.Body>
                                            {/* Source Badge */}
                                            <Box mb={2}>
                                                {getSourceBadge(agent.source_table)}
                                            </Box>

                                            {/* Agent Name and Country */}
                                            <Box mb={3}>
                                                <Heading size="md" mb={1} noOfLines={1} title={agent.agent_name} color="gray.800">
                                                    {agent.agent_name || 'No Name'}
                                                </Heading>
                                                <Badge colorPalette="red" variant="solid" borderRadius="full" px={2} py={0.5} fontSize="xs">
                                                    {agent.country}
                                                </Badge>
                                            </Box>

                                            {/* Blacklist Reason */}
                                            {agent.blacklist_reason && (
                                                <Box bg="red.50" p={3} borderRadius="md" mb={3} border="1px solid" borderColor="red.100">
                                                    <Text fontSize="sm" color="red.600" fontWeight="bold">Reason:</Text>
                                                    <Text fontSize="sm" color="gray.700" noOfLines={2}>
                                                        {agent.blacklist_reason}
                                                    </Text>
                                                </Box>
                                            )}

                                            {/* Meta Info */}
                                            <VStack align="stretch" spacing={1} fontSize="xs" color="gray.500" mb={4}>
                                                <Text>Blacklisted by: {agent.blacklisted_by}</Text>
                                                <Text>Date: {formatDate(agent.blacklisted_at)}</Text>
                                            </VStack>

                                            {/* Action Buttons */}
                                            <VStack align="stretch" spacing={2} pt={2}>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    colorPalette="purple"
                                                    width="full"
                                                    onClick={() => handleViewDetails(agent)}
                                                >
                                                    SHOW DETAILS
                                                </Button>
                                                {((activeTab === 'main' && canEditMain()) || (activeTab === 'branch' && canEditBranch())) && (
                                                    <HStack spacing={2}>
                                                        <Button
                                                            size="sm"
                                                            flex={1}
                                                            variant="outline"
                                                            colorPalette="purple"
                                                            onClick={() => handleEditAgent(agent)}
                                                        >
                                                            EDIT
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            flex={1}
                                                            variant="outline"
                                                            colorPalette="green"
                                                            onClick={() => handleRestoreFromBlacklist(agent)}
                                                        >
                                                            RESTORE
                                                        </Button>
                                                    </HStack>
                                                )}
                                                {hasRole(['Admin']) && (
                                                    <Button
                                                        size="sm"
                                                        colorPalette="red"
                                                        variant="solid"
                                                        onClick={() => handleDeleteFromBlacklist(agent)}
                                                        _hover={{ bg: 'red.700', transform: 'translateY(-1px)', shadow: 'lg' }}
                                                        transition="all 0.2s"
                                                    >
                                                        DELETE PERMANENTLY
                                                    </Button>
                                                )}
                                            </VStack>
                                        </Card.Body>
                                    </Card.Root>
                                ))}
                            </Grid>

                            {/* Pagination - Light Theme */}
                            {totalPages > 1 && (
                                <Flex justify="center" mt={8} gap={2}>
                                    <Button
                                        variant="outline"
                                        colorPalette="red"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                    >
                                        ← Previous
                                    </Button>
                                    <Flex align="center" px={4} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                                        <Text color="gray.600" fontWeight="medium">Page {page} of {totalPages}</Text>
                                    </Flex>
                                    <Button
                                        variant="outline"
                                        colorPalette="red"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                    >
                                        Next →
                                    </Button>
                                </Flex>
                            )}
                        </Box>
                    )}
                </VStack>
            </Container>

            {/* View Agent Details Modal */}
            {viewModalOpen && selectedAgent && (
                <ViewAgentDetailsModal
                    agent={selectedAgent}
                    onClose={handleCloseViewModal}
                    formatDate={formatDate}
                />
            )}

            {/* Edit Agent Modal */}
            {editModalOpen && selectedAgent && (
                <EditAgentModal
                    agent={selectedAgent}
                    onClose={handleCloseEditModal}
                    onSave={handleSaveAgent}
                    tableName="blacklist"
                    dataType={activeTab}
                />
            )}

            {/* Add to Blacklist Modal */}
            {addModalOpen && createPortal(
                <Dialog.Root open={true} onOpenChange={(e) => !e.open && setAddModalOpen(false)}>
                    <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(4px)" zIndex={9999} />
                    <Dialog.Positioner zIndex={10000}>
                        <Dialog.Content maxW="md" borderRadius="xl" boxShadow="2xl" bg="white" p={6}>
                            <Flex justify="space-between" align="center" mb={4}>
                                <Heading size="md" color="red.600" fontWeight="bold">
                                    🚫 Add to Blacklist
                                </Heading>
                                <IconButton
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setAddModalOpen(false)}
                                    aria-label="Close"
                                >
                                    <FiX />
                                </IconButton>
                            </Flex>

                            <VStack align="stretch" spacing={4}>
                                <Box>
                                    <Text fontWeight="medium" mb={2} color="gray.700">Country *</Text>
                                    <Input
                                        value={newAgent.country}
                                        onChange={(e) => setNewAgent({ ...newAgent, country: e.target.value })}
                                        placeholder="Enter country"
                                    />
                                </Box>

                                <Box>
                                    <Text fontWeight="medium" mb={2} color="gray.700">Agent Name *</Text>
                                    <Input
                                        value={newAgent.agent_name}
                                        onChange={(e) => setNewAgent({ ...newAgent, agent_name: e.target.value })}
                                        placeholder="Enter agent name"
                                    />
                                </Box>

                                <Box>
                                    <Text fontWeight="medium" mb={2} color="gray.700">Reason for blacklisting</Text>
                                    <Textarea
                                        value={newAgent.blacklist_reason}
                                        onChange={(e) => setNewAgent({ ...newAgent, blacklist_reason: e.target.value })}
                                        placeholder="Enter the reason..."
                                        rows={3}
                                    />
                                </Box>

                                <Flex gap={3} justify="flex-end" pt={2}>
                                    <Button variant="outline" onClick={() => setAddModalOpen(false)}>
                                        Cancel
                                    </Button>
                                    <Button colorPalette="red" onClick={handleAddToBlacklist}>
                                        Add to Blacklist
                                    </Button>
                                </Flex>
                            </VStack>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Dialog.Root>,
                document.body
            )}
        </Box>
    );
};

export default Blacklist;
